import {
  combineEpics,
  Epic,
  ofType,
} from "redux-observable";
import {
  distinctUntilChanged,
  filter,
  map,
  pluck,
  tap,
  withLatestFrom,
} from "rxjs/operators";

import {
  AmplitudeActions,
  LogEvent,
  LogEventFailure,
  LogEventSuccess,
  SetUserIdSuccess,
} from "./actionTypes";
import {
  bookCompleteListening,
  bookStartListening,
  logEventSuccess,
  setUserIdSuccess,
} from "./actions";
import { AMPLITUDE_LOG_EVENT } from "./constants";

import { AUDIO_PLAY, AUDIO_STOP_SUCCESS } from "../audio/constants";
import { IBookInfo } from "../library/IBookInfo";
import { getActiveRecordContent } from "../library/selectors";
import { getUser } from "../session/selectors";

import { IRootState } from "../../rootReducer";

import { amplitude } from "../../../index";
import { IUser } from "../../../types/auth/IUser";

type BookCompleteListeningEpic = Epic<AmplitudeActions, LogEvent, IRootState>;
const bookCompleteListeningEpic: BookCompleteListeningEpic = (action$, state$) => (
  action$.pipe(
    ofType(AUDIO_STOP_SUCCESS),
    withLatestFrom(state$.pipe(map(getActiveRecordContent))),
    filter(([, book]) => Boolean(book)),
    distinctUntilChanged(([, a], [, b]) => a!.id === b!.id),
    map(([, book]) => bookCompleteListening(book as IBookInfo)),
  )
);

type BookStartListeningEpic = Epic<AmplitudeActions, LogEvent, IRootState>;
const bookStartListeningEpic: BookStartListeningEpic = (action$, state$) => (
  action$.pipe(
    ofType(AUDIO_PLAY),
    withLatestFrom(state$.pipe(map(getActiveRecordContent))),
    filter(([, book]) => Boolean(book)),
    distinctUntilChanged(([, a], [, b]) => a!.id === b!.id),
    map(([, book]) => bookStartListening(book as IBookInfo)),
  )
);

type LogEventEpic = Epic<AmplitudeActions, LogEventSuccess | LogEventFailure>;
const logEventEpic: LogEventEpic = action$ => (
  action$.ofType<LogEvent>(AMPLITUDE_LOG_EVENT).pipe(
    tap(({ payload: { event, data, callback } }) => {
      amplitude.logEvent(event, data, callback);
    }),
    map(() => logEventSuccess()),
  )
);

type SetUserIdEpic = Epic<any, SetUserIdSuccess>;
const setUserIdEpic: SetUserIdEpic = (_, state$) => (
  state$.pipe(
    map(getUser),
    filter(user => Boolean(user)),
    pluck<IUser | null, string>("uid"),
    distinctUntilChanged(),
    tap((uid) => {
      amplitude.setUserId(uid);
    }),
    map(setUserIdSuccess),
  )
);

export default combineEpics(
  bookCompleteListeningEpic,
  bookStartListeningEpic,
  logEventEpic,
  setUserIdEpic,
);
